import React, { useState, useEffect, useRef } from 'react'
import { Text } from '@flatfile/baseline'
import { Header } from './Header'
import { GridAnimation, AnimationStoryboard } from '../../../components/GridAnimation/GridAnimation'

export type LoadingStage = 'initializing' | 'generating' | 'loading-space'

interface FlatfileOSLoadingScreenProps {
  stage: LoadingStage
  jobStatus?: string
}

export const FlatfileOSLoadingScreen: React.FC<FlatfileOSLoadingScreenProps> = ({ stage, jobStatus }) => {
  // Track if component has initialized
  const isInitializedRef = useRef(false)

  // Track the storyboard based on stage/jobStatus - initialize with null to ensure first animation triggers
  const [storyboard, setStoryboard] = useState<AnimationStoryboard | null>(null)

  // Initialize with thinking animation
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true
      setStoryboard('thinking')
    }
  }, [])

  // Handle animation completion - always return to thinking
  const handleAnimationComplete = (completedStoryboard: AnimationStoryboard) => {
    // When any non-thinking animation completes, return to thinking
    if (completedStoryboard !== 'thinking') {
      setStoryboard('thinking')
    }
  }

  // Determine which animation to show based on props
  useEffect(() => {
    // Don't do anything if not initialized yet
    if (!isInitializedRef.current) return

    // Default is always thinking
    let nextStoryboard: AnimationStoryboard = 'thinking'

    // Override with special animations when needed
    if (jobStatus?.toLowerCase().includes('researching')) {
      nextStoryboard = 'documentUpload'
    }
    if (stage === 'loading-space') {
      nextStoryboard = 'success'
    }

    // Only change the animation if needed
    if (storyboard !== nextStoryboard) {
      setStoryboard(nextStoryboard)
    }
  }, [stage, jobStatus, storyboard])

  // Get the content text based on current stage
  const getContentText = (): string => {
    switch (stage) {
      case 'initializing':
        return 'Initializing Flatfile OS...'
      case 'generating':
        return jobStatus?.length ? jobStatus : 'Researching company and building your app...'
      case 'loading-space':
        return 'Loading your new app...'
      default:
        return 'Setting up Flatfile OS...'
    }
  }

  return (
    <div className='flex flex-col h-full'>
      <Header showCloseButton={false} />
      <div className='flex-1 flex flex-col items-center justify-center p-8'>
        <div className='flex flex-col items-center space-y-6 max-w-md'>
          {!!storyboard && (
            <>
              <div className='text-center space-y-4 pb-[55px]'>
                <Text variant='largeTitle'>{getContentText()}</Text>
              </div>
              <GridAnimation
                storyboard={storyboard}
                className='opacity-50'
                onAnimationComplete={handleAnimationComplete}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
